import {
	PathSlopeType,
	type IGeometry,
	type ISavedRoadData,
	type ISurveyWGS84Point,
	type TVerticalAlignmentData,
	type THorizontalAlignmentData,
	HorizontalAlignmentType,
} from "earthbrain-viewer";
import {
	type ICrossSectionSettings,
	type IObject,
	type IObjectData,
	ObjectType,
} from "./types";
import { Color, createGuid } from "cesium";
import store from "@/store";
import { defaultLineWorksCrossSectionByDistanceUnit } from "@/components/Project/LineWorks/default";
import type { IRoadObjectData_Version3 } from "./RoadObjectAdapter";
import { uploadObjAndNotify } from "@/utils/cesium-common";
import { md5 } from "hash-wasm";
import { i18n } from "@/i18n.js";
import { distanceUnits, type DistanceUnit } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
// from server differet schema data will come
// adapter will convert that to latest version
// latest version is the combination of data for UI and viewer
// for viewer,
// this class will be used to update the object

//phase 1: Make sure road object can be used in UI
//phase 2: Make sure road object can be used in viewer
//phase 3: Update to merge data from UI and viewer
//phase 4: Modify viewer to use the new data structure

// issues found
// 1. Sync data between multiple users
// 2. Make straight does not work with if arc is found

type RoadDistanceDefaults = {
	leftWidth: number;
	rightWidth: number;
	interpPitch: number;
	cutRoadHeight: number;
	cutShelfWidth: number;
	cutLeftSlopeHeight: number;
	cutRightSlopeHeight: number;
	cutLeftShelfWidth: number;
	cutRightShelfWidth: number;
	fillRoadHeight: number;
	fillShelfWidth: number;
	fillLeftSlopeHeight: number;
	fillRightSlopeHeight: number;
	fillLeftShelfWidth: number;
	fillRightShelfWidth: number;
};

const ROAD_FEET_DISPLAY_DEFAULTS: RoadDistanceDefaults = {
	leftWidth: 12,
	rightWidth: 12,
	interpPitch: 16,
	cutRoadHeight: 16,
	cutShelfWidth: 5,
	cutLeftSlopeHeight: 16,
	cutRightSlopeHeight: 16,
	cutLeftShelfWidth: 5,
	cutRightShelfWidth: 5,
	fillRoadHeight: 16,
	fillShelfWidth: 5,
	fillLeftSlopeHeight: 16,
	fillRightSlopeHeight: 16,
	fillLeftShelfWidth: 5,
	fillRightShelfWidth: 5,
};

/** Display defaults per distance unit (distance fields only; dimensionless params excluded) */
const ROAD_DISPLAY_DEFAULTS: Record<DistanceUnit, RoadDistanceDefaults> = {
	[distanceUnits.METERS]: {
		leftWidth: 2.5,
		rightWidth: 2.5,
		interpPitch: 5,
		cutRoadHeight: 5,
		cutShelfWidth: 1.5,
		cutLeftSlopeHeight: 5,
		cutRightSlopeHeight: 5,
		cutLeftShelfWidth: 1.5,
		cutRightShelfWidth: 1.5,
		fillRoadHeight: 5,
		fillShelfWidth: 1.5,
		fillLeftSlopeHeight: 5,
		fillRightSlopeHeight: 5,
		fillLeftShelfWidth: 1.5,
		fillRightShelfWidth: 1.5,
	},
	[distanceUnits.FEET]: ROAD_FEET_DISPLAY_DEFAULTS,
	[distanceUnits.US_SURVEY_FEET]: ROAD_FEET_DISPLAY_DEFAULTS,
};

export interface IRoadObjectData extends IObjectData {
	panel: {
		road: boolean;
		cut: boolean;
		fill: boolean;
		volume: boolean;
	};
	cutVolume: number;
	fillVolume: number;
	transparency: number;
	length: number;
	heightRange: {
		min: number;
		max: number;
	};
	slopeRange: {
		min: number;
		max: number;
	};
	pathSettings: {
		leftWidth: number;
		rightWidth: number;
		leftSlope: number;
		rightSlope: number;
		isSuperElevation: boolean;
		interpolationPitch: number;
		surfaceColor: string;
	};
	cutSettings: {
		roadHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};
	fillSettings: {
		roadHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};

	surveyPoints: ISurveyWGS84Point[];
	ipPointIds: string[];
	horizontalAlignment: THorizontalAlignmentData;
	verticalAlignment: TVerticalAlignmentData[];
	crossSectionSetting?: ICrossSectionSettings;
}

export class RoadObject implements IObject, IRoadObjectData {
	readonly type: ObjectType = ObjectType.ROAD;
	id: string;
	md5: string;
	name: string;
	isOpen: boolean;
	panel: {
		road: boolean;
		cut: boolean;
		fill: boolean;
		volume: boolean;
	};
	cutVolume: number;
	fillVolume: number;
	transparency: number;

	length: number;
	heightRange: {
		min: number;
		max: number;
	};
	slopeRange: {
		min: number;
		max: number;
	};

	pathSettings: {
		leftWidth: number;
		rightWidth: number;
		leftSlope: number;
		rightSlope: number;
		isSuperElevation: boolean;
		interpolationPitch: number;
		surfaceColor: string;
	};
	cutSettings: {
		roadHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};
	fillSettings: {
		roadHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};
	isLoaded: boolean;
	isHiddenDuringEdit: boolean;
	loadingVolume: boolean;
	associatedTerrain: { id: number; terrainType: string };
	surveyPoints: ISurveyWGS84Point[];
	ipPointIds: string[];
	horizontalAlignment: THorizontalAlignmentData;
	verticalAlignment: TVerticalAlignmentData[];

	showSlope2DInput: boolean;
	slope2DInput: {
		slopeValue: number;
		slopeUnit: string;
		slopeIndex: number;
		clientX: number;
		clientY: number;
	};
	crossSectionSetting: ICrossSectionSettings;
	isVisible: {
		ipLine: boolean;
		centerLine: boolean;
		crossSection: boolean;
		surface: boolean;
		cutEmbankment: boolean;
		fillEmbankment: boolean;
	};

	constructor() {
		this.isLoaded = false;
		this.isHiddenDuringEdit = false;
		this.isOpen = false;
		this.isVisible = {
			ipLine: false,
			centerLine: false,
			crossSection: false,
			surface: false,
			cutEmbankment: false,
			fillEmbankment: false,
		};
		this.loadingVolume = false;
		this.id = this.default.id;
		this.md5 = this.default.md5;
		this.name = this.default.name;
		this.panel = this.default.panel;
		this.cutVolume = this.default.cutVolume;
		this.fillVolume = this.default.fillVolume;
		this.transparency = this.default.transparency;
		this.length = this.default.length;
		this.heightRange = this.default.heightRange;
		this.slopeRange = this.default.slopeRange;
		this.pathSettings = this.default.pathSettings;
		this.cutSettings = this.default.cutSettings;
		this.fillSettings = this.default.fillSettings;
		this.associatedTerrain = {
			id: this.default.associatedTerrain.id,
			terrainType: this.default.associatedTerrain.terrainType,
		};
		this.slope2DInput = {
			slopeValue: 0,
			slopeIndex: 0,
			slopeUnit: "",
			clientX: 0,
			clientY: 0,
		};
		this.showSlope2DInput = false;
		this.surveyPoints = [];
		this.ipPointIds = [];
		this.horizontalAlignment = this.default.horizontalAlignment;
		this.verticalAlignment = [];
		this.crossSectionSetting = this.default.crossSectionSetting;
	}

	get default(): IRoadObjectData {
		const unit = store.state.site?.units?.distance ?? distanceUnits.METERS;
		const defaults = ROAD_DISPLAY_DEFAULTS[unit];
		const unitPort = createDistanceUnitPort(unit);
		const toInternal = (v: number) => unitPort.toMeters(v);

		return {
			id: null,
			type: ObjectType.ROAD,
			md5: "",
			name: "",
			panel: {
				road: false,
				cut: false,
				fill: false,
				volume: false,
			},
			cutVolume: 0,
			fillVolume: 0,
			transparency: 1,
			length: 0,
			heightRange: {
				min: 0,
				max: 0,
			},
			slopeRange: {
				min: 0,
				max: 0,
			},
			pathSettings: {
				leftWidth: toInternal(defaults.leftWidth),
				rightWidth: toInternal(defaults.rightWidth),
				leftSlope: 50,
				rightSlope: 50,
				isSuperElevation: false,
				interpolationPitch: toInternal(defaults.interpPitch),
				surfaceColor: "#DDDDDD",
			},
			cutSettings: {
				roadHeight: toInternal(defaults.cutRoadHeight),
				moundSlope: 1.5,
				shelfWidth: toInternal(defaults.cutShelfWidth),
				shelfSlope: 100,
				isLeftRightDifferent: false,
				generateShelf: true,
				leftSlopeHeight: toInternal(defaults.cutLeftSlopeHeight),
				leftMoundSlope: 1,
				rightSlopeHeight: toInternal(defaults.cutRightSlopeHeight),
				rightMoundSlope: 1,
				leftShelfWidth: toInternal(defaults.cutLeftShelfWidth),
				leftShelfSlope: 100,
				rightShelfWidth: toInternal(defaults.cutRightShelfWidth),
				rightShelfSlope: 100,
				color: "#FFA500",
				outline: true,
			},
			fillSettings: {
				roadHeight: toInternal(defaults.fillRoadHeight),
				moundSlope: 1.5,
				shelfWidth: toInternal(defaults.fillShelfWidth),
				shelfSlope: 100,
				isLeftRightDifferent: false,
				generateShelf: true,
				leftSlopeHeight: toInternal(defaults.fillLeftSlopeHeight),
				leftMoundSlope: 1.5,
				rightSlopeHeight: toInternal(defaults.fillRightSlopeHeight),
				rightMoundSlope: 1.5,
				leftShelfWidth: toInternal(defaults.fillLeftShelfWidth),
				leftShelfSlope: 100,
				rightShelfWidth: toInternal(defaults.fillRightShelfWidth),
				rightShelfSlope: 100,
				color: "#008000",
				outline: true,
			},
			associatedTerrain: { id: null, terrainType: "" },
			surveyPoints: [],
			ipPointIds: [],
			horizontalAlignment: null,
			verticalAlignment: [],
			crossSectionSetting: defaultLineWorksCrossSectionByDistanceUnit(
				unit,
				toInternal,
			),
		};
	}

	get checkIfRoadShouldBeUpdated() {
		return (
			this.horizontalAlignment?.type ===
			HorizontalAlignmentType.QUADRATIC_BEZIER
		);
	}

	get viewerLineId() {
		return this.id;
	}

	get lineName() {
		return this.name;
	}

	get isShow() {
		return (
			this.isVisible.ipLine ||
			this.isVisible.centerLine ||
			this.isVisible.surface ||
			this.isVisible.cutEmbankment ||
			this.isVisible.fillEmbankment
		);
	}

	get isEmbankmentVisible() {
		return this.isVisible.cutEmbankment || this.isVisible.fillEmbankment;
	}

	get isAlignmentVisible() {
		return this.isVisible.ipLine || this.isVisible.centerLine;
	}

	/**
	 * Update the object with the new data
	 * @param data - The new data to update the object with
	 * Update the object in viewer if it has been loaded in viewer
	 */
	async update(
		data: Partial<IRoadObjectData>,
		updateInViewer: boolean = false,
	) {
		this.id = data.id ?? this.id;
		this.md5 = data.md5 ?? this.md5;
		this.name = data.name ?? this.name ?? this.default.name;
		this.panel.road =
			data.panel?.road ?? this.panel.road ?? this.default.panel.road;
		this.panel.cut =
			data.panel?.cut ?? this.panel.cut ?? this.default.panel.cut;
		this.panel.fill =
			data.panel?.fill ?? this.panel.fill ?? this.default.panel.fill;
		this.panel.volume =
			data.panel?.volume ?? this.panel.volume ?? this.default.panel.volume;
		this.cutVolume = data.cutVolume ?? this.cutVolume ?? this.default.cutVolume;
		this.fillVolume =
			data.fillVolume ?? this.fillVolume ?? this.default.fillVolume;
		this.transparency =
			data.transparency ?? this.transparency ?? this.default.transparency;
		this.length = data.length ?? this.length ?? this.default.length;
		this.heightRange =
			data.heightRange ?? this.heightRange ?? this.default.heightRange;
		this.slopeRange =
			data.slopeRange ?? this.slopeRange ?? this.default.slopeRange;
		this.pathSettings.leftWidth =
			data.pathSettings.leftWidth ??
			this.pathSettings.leftWidth ??
			this.default.pathSettings.leftWidth;
		this.pathSettings.rightWidth =
			data.pathSettings.rightWidth ??
			this.pathSettings.rightWidth ??
			this.default.pathSettings.rightWidth;
		this.pathSettings.leftSlope =
			data.pathSettings.leftSlope ??
			this.pathSettings.leftSlope ??
			this.default.pathSettings.leftSlope;
		this.pathSettings.isSuperElevation =
			data.pathSettings.isSuperElevation ??
			this.pathSettings.isSuperElevation ??
			this.default.pathSettings.isSuperElevation;
		this.pathSettings.interpolationPitch =
			data.pathSettings.interpolationPitch ??
			this.pathSettings.interpolationPitch ??
			this.default.pathSettings.interpolationPitch;
		this.pathSettings.surfaceColor =
			data.pathSettings.surfaceColor ??
			this.pathSettings.surfaceColor ??
			this.default.pathSettings.surfaceColor;

		this.cutSettings.roadHeight =
			data.cutSettings.roadHeight ??
			this.cutSettings.roadHeight ??
			this.default.cutSettings.roadHeight;
		this.cutSettings.moundSlope =
			data.cutSettings.moundSlope ??
			this.cutSettings.moundSlope ??
			this.default.cutSettings.moundSlope;
		this.cutSettings.shelfWidth =
			data.cutSettings.shelfWidth ??
			this.cutSettings.shelfWidth ??
			this.default.cutSettings.shelfWidth;
		this.cutSettings.shelfSlope =
			data.cutSettings.shelfSlope ??
			this.cutSettings.shelfSlope ??
			this.default.cutSettings.shelfSlope;
		this.cutSettings.isLeftRightDifferent =
			data.cutSettings.isLeftRightDifferent ??
			this.cutSettings.isLeftRightDifferent ??
			this.default.cutSettings.isLeftRightDifferent;
		this.cutSettings.generateShelf =
			data.cutSettings.generateShelf ??
			this.cutSettings.generateShelf ??
			this.default.cutSettings.generateShelf;
		this.cutSettings.leftSlopeHeight =
			data.cutSettings.leftSlopeHeight ??
			this.cutSettings.leftSlopeHeight ??
			this.default.cutSettings.leftSlopeHeight;
		this.cutSettings.leftMoundSlope =
			data.cutSettings.leftMoundSlope ??
			this.cutSettings.leftMoundSlope ??
			this.default.cutSettings.leftMoundSlope;
		this.cutSettings.rightSlopeHeight =
			data.cutSettings.rightSlopeHeight ??
			this.cutSettings.rightSlopeHeight ??
			this.default.cutSettings.rightSlopeHeight;
		this.cutSettings.rightMoundSlope =
			data.cutSettings.rightMoundSlope ??
			this.cutSettings.rightMoundSlope ??
			this.default.cutSettings.rightMoundSlope;
		this.cutSettings.leftShelfWidth =
			data.cutSettings.leftShelfWidth ??
			this.cutSettings.leftShelfWidth ??
			this.default.cutSettings.leftShelfWidth;
		this.cutSettings.leftShelfSlope =
			data.cutSettings.leftShelfSlope ??
			this.cutSettings.leftShelfSlope ??
			this.default.cutSettings.leftShelfSlope;
		this.cutSettings.rightShelfWidth =
			data.cutSettings.rightShelfWidth ??
			this.cutSettings.rightShelfWidth ??
			this.default.cutSettings.rightShelfWidth;
		this.cutSettings.rightShelfSlope =
			data.cutSettings.rightShelfSlope ??
			this.cutSettings.rightShelfSlope ??
			this.default.cutSettings.rightShelfSlope;
		this.cutSettings.color =
			data.cutSettings.color ??
			this.cutSettings.color ??
			this.default.cutSettings.color;
		this.cutSettings.outline =
			data.cutSettings.outline ??
			this.cutSettings.outline ??
			this.default.cutSettings.outline;

		this.fillSettings.roadHeight =
			data.fillSettings.roadHeight ??
			this.fillSettings.roadHeight ??
			this.default.fillSettings.roadHeight;
		this.fillSettings.moundSlope =
			data.fillSettings.moundSlope ??
			this.fillSettings.moundSlope ??
			this.default.fillSettings.moundSlope;
		this.fillSettings.shelfWidth =
			data.fillSettings.shelfWidth ??
			this.fillSettings.shelfWidth ??
			this.default.fillSettings.shelfWidth;
		this.fillSettings.shelfSlope =
			data.fillSettings.shelfSlope ??
			this.fillSettings.shelfSlope ??
			this.default.fillSettings.shelfSlope;
		this.fillSettings.isLeftRightDifferent =
			data.fillSettings.isLeftRightDifferent ??
			this.fillSettings.isLeftRightDifferent ??
			this.default.fillSettings.isLeftRightDifferent;
		this.fillSettings.generateShelf =
			data.fillSettings.generateShelf ??
			this.fillSettings.generateShelf ??
			this.default.fillSettings.generateShelf;
		this.fillSettings.leftSlopeHeight =
			data.fillSettings.leftSlopeHeight ??
			this.fillSettings.leftSlopeHeight ??
			this.default.fillSettings.leftSlopeHeight;
		this.fillSettings.leftMoundSlope =
			data.fillSettings.leftMoundSlope ??
			this.fillSettings.leftMoundSlope ??
			this.default.fillSettings.leftMoundSlope;
		this.fillSettings.rightSlopeHeight =
			data.fillSettings.rightSlopeHeight ??
			this.fillSettings.rightSlopeHeight ??
			this.default.fillSettings.rightSlopeHeight;
		this.fillSettings.rightMoundSlope =
			data.fillSettings.rightMoundSlope ??
			this.fillSettings.rightMoundSlope ??
			this.default.fillSettings.rightMoundSlope;
		this.fillSettings.leftShelfWidth =
			data.fillSettings.leftShelfWidth ??
			this.fillSettings.leftShelfWidth ??
			this.default.fillSettings.leftShelfWidth;
		this.fillSettings.leftShelfSlope =
			data.fillSettings.leftShelfSlope ??
			this.fillSettings.leftShelfSlope ??
			this.default.fillSettings.leftShelfSlope;
		this.fillSettings.rightShelfWidth =
			data.fillSettings.rightShelfWidth ??
			this.fillSettings.rightShelfWidth ??
			this.default.fillSettings.rightShelfWidth;
		this.fillSettings.rightShelfSlope =
			data.fillSettings.rightShelfSlope ??
			this.fillSettings.rightShelfSlope ??
			this.default.fillSettings.rightShelfSlope;
		this.fillSettings.color =
			data.fillSettings.color ??
			this.fillSettings.color ??
			this.default.fillSettings.color;
		this.fillSettings.outline =
			data.fillSettings.outline ??
			this.fillSettings.outline ??
			this.default.fillSettings.outline;
		this.associatedTerrain.id =
			data.associatedTerrain.id ??
			this.associatedTerrain.id ??
			this.default.associatedTerrain.id;
		this.associatedTerrain.terrainType =
			data.associatedTerrain.terrainType ??
			this.associatedTerrain.terrainType ??
			this.default.associatedTerrain.terrainType;
		this.surveyPoints =
			data.surveyPoints ?? this.surveyPoints ?? this.default.surveyPoints;
		this.ipPointIds =
			data.ipPointIds ?? this.ipPointIds ?? this.default.ipPointIds;
		this.horizontalAlignment =
			data.horizontalAlignment ??
			this.horizontalAlignment ??
			this.default.horizontalAlignment;
		this.verticalAlignment =
			data.verticalAlignment ??
			this.verticalAlignment ??
			this.default.verticalAlignment;

		this.pathSettings.rightSlope = this.pathSettings.isSuperElevation
			? -1 * this.pathSettings.leftSlope
			: this.pathSettings.leftSlope;

		if (!this.fillSettings.isLeftRightDifferent) {
			this.fillSettings.leftMoundSlope = this.fillSettings.moundSlope;
			this.fillSettings.rightMoundSlope = this.fillSettings.moundSlope;
			this.fillSettings.leftShelfSlope = this.fillSettings.shelfSlope;
			this.fillSettings.rightShelfSlope = this.fillSettings.shelfSlope;
			this.fillSettings.leftShelfWidth = this.fillSettings.shelfWidth;
			this.fillSettings.rightShelfWidth = this.fillSettings.shelfWidth;
			this.fillSettings.leftSlopeHeight = this.fillSettings.roadHeight;
			this.fillSettings.rightSlopeHeight = this.fillSettings.roadHeight;
		}

		if (!this.cutSettings.isLeftRightDifferent) {
			this.cutSettings.leftMoundSlope = this.cutSettings.moundSlope;
			this.cutSettings.rightMoundSlope = this.cutSettings.moundSlope;
			this.cutSettings.leftShelfSlope = this.cutSettings.shelfSlope;
			this.cutSettings.rightShelfSlope = this.cutSettings.shelfSlope;
			this.cutSettings.leftShelfWidth = this.cutSettings.shelfWidth;
			this.cutSettings.rightShelfWidth = this.cutSettings.shelfWidth;
			this.cutSettings.leftSlopeHeight = this.cutSettings.roadHeight;
			this.cutSettings.rightSlopeHeight = this.cutSettings.roadHeight;
		}

		this.crossSectionSetting =
			data.crossSectionSetting ??
			this.crossSectionSetting ??
			this.default.crossSectionSetting;

		if (updateInViewer) {
			await this.updateSettingsInViewer();
		}
	}

	public async updateMaterialSettings() {
		await store.getters.viewer.updateRoadMaterialSettings(
			this.id,
			{ surfaceColor: this.pathSettings.surfaceColor },
			{ color: this.fillSettings.color },
			{ color: this.cutSettings.color },
		);
	}

	public async updateTransparency() {
		await store.getters.viewer.updateRoadMaterialSettings(
			this.id,
			{},
			{ transparency: this.transparency },
			{ transparency: this.transparency },
		);
	}

	private getSettingsForViewer() {
		const commonPathSettings = {
			leftWidth: this.pathSettings.leftWidth,
			rightWidth: this.pathSettings.rightWidth,
			interpolationPitch: this.pathSettings.interpolationPitch,
			color: this.pathSettings.surfaceColor,
			transparency: this.transparency,
			outline: this.cutSettings.outline,
			controlLineSettings: {
				width: 1,
				color: Color.SKYBLUE.withAlpha(0.5),
				outlineColor: Color.RED,
				outlineWidth: 3,
				clampToGround: false,
				epsilonGapBetWeenMeshAndPolyline: 0.1,
			},
			polyLineSettings: {
				width: 9,
				color: Color.SKYBLUE.withAlpha(0.5),
				outlineColor: Color.BLUE,
				outlineWidth: 3,
				clampToGround: false,
				epsilonGapBetWeenMeshAndPolyline: 0.1,
			},
			centerLineSettings: {
				width: 3,
				clampToGround: false,
				color: Color.BLACK,
			},
		};
		const settingsForViewer = {
			commonSetting: {
				path: this.pathSettings.isSuperElevation
					? {
							roadSlopeType: PathSlopeType.STRAIGHT as PathSlopeType.STRAIGHT,
							slope: this.pathSettings.leftSlope,
							...commonPathSettings,
						}
					: {
							roadSlopeType: PathSlopeType.CROSS as PathSlopeType.CROSS,
							leftSlope: this.pathSettings.leftSlope,
							rightSlope: this.pathSettings.rightSlope,
							...commonPathSettings,
						},
				cutSetting: {
					generateShelf: this.cutSettings.generateShelf,
					left: {
						slopeHeight: this.cutSettings.leftSlopeHeight,
						moundSlope: this.cutSettings.leftMoundSlope,
						shelfWidth: this.cutSettings.leftShelfWidth,
						shelfSlope: this.cutSettings.leftShelfSlope,
					},
					right: {
						slopeHeight: this.cutSettings.rightSlopeHeight,
						moundSlope: this.cutSettings.rightMoundSlope,
						shelfWidth: this.cutSettings.rightShelfWidth,
						shelfSlope: this.cutSettings.rightShelfSlope,
					},
					color: this.cutSettings.color,
					transparency: this.transparency,
				},
				embarkmentSetting: {
					generateShelf: this.fillSettings.generateShelf,
					left: {
						slopeHeight: this.fillSettings.leftSlopeHeight,
						moundSlope: this.fillSettings.leftMoundSlope,
						shelfWidth: this.fillSettings.leftShelfWidth,
						shelfSlope: this.fillSettings.leftShelfSlope,
					},
					right: {
						slopeHeight: this.fillSettings.rightSlopeHeight,
						moundSlope: this.fillSettings.rightMoundSlope,
						shelfWidth: this.fillSettings.rightShelfWidth,
						shelfSlope: this.fillSettings.rightShelfSlope,
					},
					color: this.fillSettings.color,
					transparency: this.transparency,
				},
			},
		};
		return settingsForViewer;
	}

	private getViewerJSON(): ISavedRoadData {
		return {
			roadId: this.id,
			roadSettings: this.getSettingsForViewer() as any,
			surveyPoints: this.surveyPoints,
			ipPointIds: this.ipPointIds,
			horizontalAlignment: this.horizontalAlignment,
			verticalAlignment: this.verticalAlignment,
			associatedTerrain: {
				id: this.associatedTerrain.id,
				terrainType: this.associatedTerrain.terrainType as any,
			},
			rubbingDestination: {
				target: this.associatedTerrain.terrainType as any,
				id: this.associatedTerrain.id,
			},
		};
	}

	async loadInViewer(): Promise<void> {
		try {
			const viewerJSON = this.getViewerJSON();
			this.id = await store.getters.viewer.loadRoadJSON(
				viewerJSON,
				this.crossSectionSetting.interpolationPitch,
			);
			this.updateFromViewer();
			this.isLoaded = true;
			this.isHiddenDuringEdit = false;
			this.isVisible = {
				ipLine: true,
				centerLine: false,
				crossSection: false,
				surface: true,
				cutEmbankment: true,
				fillEmbankment: true,
			};
		} catch (e) {
			console.error(e);
			this.isLoaded = false;
			this.isVisible = {
				ipLine: false,
				centerLine: false,
				crossSection: false,
				surface: false,
				cutEmbankment: false,
				fillEmbankment: false,
			};
			this.isHiddenDuringEdit = false;
		}
	}

	private shouldBlockRestoreOnInvalidEditForm(): boolean {
		return (
			store.state.object.objectUnderEdit?.id === this.id &&
			store.state.object.isEditFormValid === false
		);
	}

	private showInvalidEditFormSnackbar(): void {
		store.commit("set_snackbar", {
			text: i18n.global.t("CANNOT_SHOW_OBJECT_INVALID_VALUES"),
			color: "rgba(153, 0, 0, 0.72)",
			timeout: -1,
		});
	}

	private async loadRoad() {
		await this.loadInViewer();
		if (!this.isLoaded) {
			// Loading failed; do not attempt to toggle visibility
			return;
		}
		if (!this.crossSectionSetting.id) {
			const savedCrossSection = await store.dispatch(
				"lineWorksCrossSection/saveCrossSectionOnCreate",
				{
					object: this,
					siteId: store.getters.site.site_uuid,
					crossSectionParams: this.crossSectionSetting,
				},
				{ root: true },
			);
			this.crossSectionSetting.id = savedCrossSection.cross_section_id;
		}
	}

	private hideAllVisibles() {
		store.getters.viewer.toggleRoadVisibility(this.id, false);
		this.isVisible = {
			ipLine: false,
			centerLine: false,
			crossSection: false,
			surface: false,
			cutEmbankment: false,
			fillEmbankment: false,
		};
	}

	async setVisibility(visible: boolean): Promise<boolean> {
		if (visible && !this.isLoaded) {
			if (this.shouldBlockRestoreOnInvalidEditForm()) {
				this.showInvalidEditFormSnackbar();
				return false;
			}
			await this.loadRoad();
			if (!this.isLoaded) {
				// Loading failed; do not attempt to toggle visibility
				return false;
			}
			return true;
		}
		store.getters.viewer.toggleRoadVisibility(this.id, visible);
		this.isVisible.surface = visible;
		this.isVisible.cutEmbankment = visible;
		this.isVisible.fillEmbankment = visible;
		this.isVisible.ipLine = visible;
		if (!visible) {
			await this.setCenterLineVisibility(false);
		}
		if (!visible && store.state.object.objectUnderEdit?.id === this.id) {
			this.isHiddenDuringEdit = true;
		}
		return true;
	}

	async startEdit(): Promise<void> {
		// ask user if data needs to be updated to straight or default
		let result: string | null = null;
		if (this.checkIfRoadShouldBeUpdated) {
			result = await store.dispatch("object/openObjectUpdateDialog");
			if (result === null) {
				throw new Error("User cancelled to update old road", {
					cause: {
						code: "CANCELLED_BY_USER",
						message: "Road cannot be edited unless updated to new road",
					},
				});
			}
		}
		store.getters.viewer.enableRoadEdit(
			true,
			this.id,
			async (_id, sectionMarkerLabel) => {
				this.updateFromViewer();
				// undefined means "label unchanged" (e.g. after a height edit); only endEdit clears it.
				if (typeof sectionMarkerLabel === "string") {
					store.commit("setActiveSectionMarkerLabel", sectionMarkerLabel);
				}
			},
			(event, data) => {
				this.showSlope2DInput = true;
				// console.log("from client", event, data)
				this.slope2DInput.slopeValue = data.slopeValue;
				this.slope2DInput.slopeIndex = data.slopeIndex;
				this.slope2DInput.slopeUnit = data.slopeUnit;
				this.slope2DInput.clientX = event.clientX;
				this.slope2DInput.clientY = event.clientY;
			},
		);
		result &&
			store.getters.viewer.updateRoadHorizontalAlignmentType(this.id, result);
	}

	async endEdit(): Promise<void> {
		if (this.isVisible.crossSection) {
			await this.setCrossSectionVisibility(false);
		}
		await store.getters.viewer.updateAlignmentStationInterval(
			this.id,
			this.crossSectionSetting.interpolationPitch,
		);
		await store.getters.viewer.enableRoadEdit(false, this.id);
		store.commit("setActiveSectionMarkerLabel", "");
		this.updateFromViewer();
		const propsJSON = await this.toJSON();
		await uploadObjAndNotify(propsJSON, this.type);
		await store.dispatch(
			"lineWorksCrossSection/saveCrossSectionOfLineWorks",
			{
				crossSectionSetting: {
					...this.crossSectionSetting,
					name: this.name,
				},
			},
			{ root: true },
		);
	}

	deleteObject(): void {
		store.getters.viewer.deleteRoad(this.id);
	}

	getGeometry(): IGeometry {
		return store.getters.viewer.getRoadMeshGeometry(this.id);
	}

	fitToView(): void {
		store.getters.viewer.fitToRoad(this.id);
	}

	private async updateSettingsInViewer() {
		console.log("updateSettingsInViewer called");
		await store.getters.viewer.updateRoadSettings(
			this.id,
			this.getSettingsForViewer(),
		);

		this.updateFromViewer();
	}

	updateFromViewer() {
		const roadViewerObj: ISavedRoadData = store.getters.viewer.getRoadJSON(
			this.id,
		);
		if (!roadViewerObj) {
			return;
		}
		this.surveyPoints = roadViewerObj.surveyPoints;
		this.ipPointIds = roadViewerObj.ipPointIds;
		this.horizontalAlignment = roadViewerObj.horizontalAlignment;
		this.verticalAlignment = roadViewerObj.verticalAlignment;
		const lengthOfRoad = store.getters.viewer.getLengthOfRoad(this.id);
		if (lengthOfRoad) {
			this.length = Number.parseFloat(lengthOfRoad.toFixed(3));
		}
		this.heightRange = store.getters.viewer.getRoadHeightInfo(this.id);
		this.slopeRange = store.getters.viewer.getRoadSlopeInfo(this.id);
		this.associatedTerrain = {
			id: roadViewerObj.associatedTerrain.id,
			terrainType: roadViewerObj.associatedTerrain.terrainType as any,
		};
	}

	async updateAssociatedTerrain(terrainId: number, terrainType: string) {
		this.associatedTerrain = {
			id: terrainId,
			terrainType: terrainType as any,
		};
		await store.getters.viewer.updateTerrainAssociatedWithRoad(
			this.id,
			terrainId,
			terrainType as any,
		);
		await store.getters.viewer.updateRubbingDestinationAssociatedWithRoad(
			this.id,
			{
				target: terrainType as any,
				id: terrainId,
			},
		);
	}

	async setCenterLineVisibility(visible: boolean) {
		if (this.isVisible.centerLine === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setRoadCenterLineVisibility(this.id, visible);
		this.isVisible.centerLine = visible;
		if (!this.isVisible.centerLine) {
			await this.setCrossSectionVisibility(false);
		}
	}

	async setIpLineVisibility(visible: boolean) {
		if (this.isVisible.ipLine === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setRoadIpLineVisibility(this.id, visible);
		this.isVisible.ipLine = visible;
	}

	async setAlignmentVisibility(visible: boolean) {
		if (this.isAlignmentVisible === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
			this.hideAllVisibles();
		}
		await this.setIpLineVisibility(visible);
		await this.setCenterLineVisibility(visible);
	}

	async setCrossSectionVisibility(visible: boolean) {
		if (this.isVisible.crossSection === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
		}
		if (visible) {
			await this.setVisibility(true);
			await this.setCenterLineVisibility(true);
			await store.getters.viewer.generateAlignmentCrossSection(
				this.id,
				this.crossSectionSetting,
			);
			this.isVisible.crossSection = visible;
		} else {
			await store.getters.viewer.disposeLineWorksCrossSection(this.id);
			this.isVisible.crossSection = visible;
		}
	}

	async setSurfaceVisibility(visible: boolean) {
		if (this.isVisible.surface === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setRoadSurfaceVisibility(this.id, visible);
		this.isVisible.surface = visible;
	}

	async setEmbankmentVisibility(visible: boolean) {
		if (this.isEmbankmentVisible === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setRoadEmbankmentVisibility(this.id, visible);
		this.isVisible.fillEmbankment = visible;
		this.isVisible.cutEmbankment = visible;
	}

	async setFillEmbankmentVisibility(visible: boolean) {
		if (this.isVisible.fillEmbankment === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setRoadFillEmbankmentVisibility(
			this.id,
			visible,
		);
		this.isVisible.fillEmbankment = visible;
	}

	async setCutEmbankmentVisibility(visible: boolean) {
		if (this.isVisible.cutEmbankment === visible) return;
		if (visible && !this.isLoaded) {
			await this.loadRoad();
			this.hideAllVisibles();
		}
		await store.getters.viewer.setRoadCutEmbankmentVisibility(this.id, visible);
		this.isVisible.cutEmbankment = visible;
	}

	async updateCrossSection() {
		await store.getters.viewer.generateAlignmentCrossSection(
			this.id,
			this.crossSectionSetting,
		);
	}

	async toJSON(): Promise<IRoadObjectData_Version3> {
		this.updateFromViewer();
		const json = {
			id: this.id,
			cid: this.id,
			type: this.type,
			name: this.name,
			panel: this.panel,
			cutVolume: this.cutVolume,
			fillVolume: this.fillVolume,
			transparency: this.transparency,
			length: this.length,
			heightRange: this.heightRange,
			slopeRange: this.slopeRange,
			pathSettings: this.pathSettings,
			cutSettings: this.cutSettings,
			fillSettings: this.fillSettings,
			associatedTerrain: this.associatedTerrain,
			surveyPoints: this.surveyPoints,
			ipPointIds: this.ipPointIds,
			horizontalAlignment: this.horizontalAlignment,
			verticalAlignment: this.verticalAlignment,
		};

		let hash: string;
		try {
			hash = await md5(JSON.stringify(json));
		} catch (error) {
			console.error("Failed to generate MD5 hash:", error);
			hash = "M85FailedToGenerateMD5Hash"; // Or generate a fallback identifier
		}

		return {
			version: 3.0, // json schema version
			...json,
			md5: hash,
		};
	}

	async toDuplicateJSON(): Promise<IRoadObjectData_Version3> {
		this.updateFromViewer();
		const surveyIdMap = new Map<string, string>();
		this.surveyPoints.forEach((surveyPoint) => {
			surveyIdMap.set(surveyPoint.id, createGuid());
		});
		const newSurveyPoints = this.surveyPoints.map((surveyPoint) => {
			return {
				...surveyPoint,
				id: surveyIdMap.get(surveyPoint.id),
			};
		});
		const newIpPointIds = this.ipPointIds.map((ipPointId) => {
			return surveyIdMap.get(ipPointId);
		});
		const newId = crypto.randomUUID();
		const json = {
			id: newId,
			cid: newId,
			type: this.type,
			name: this.name + " copy",
			panel: this.panel,
			cutVolume: this.cutVolume,
			fillVolume: this.fillVolume,
			transparency: this.transparency,
			length: this.length,
			heightRange: this.heightRange,
			slopeRange: this.slopeRange,
			pathSettings: this.pathSettings,
			cutSettings: this.cutSettings,
			fillSettings: this.fillSettings,
			associatedTerrain: this.associatedTerrain,
			surveyPoints: newSurveyPoints,
			ipPointIds: newIpPointIds,
			horizontalAlignment: this.horizontalAlignment,
			verticalAlignment: this.verticalAlignment,
		};

		let hash: string;
		try {
			hash = await md5(JSON.stringify(json));
		} catch (error) {
			console.error("Failed to generate MD5 hash:", error);
			hash = "M85FailedToGenerateMD5Hash"; // Or generate a fallback identifier
		}

		return {
			version: 3.0, // json schema version
			...json,
			md5: hash,
		};
	}
}
